import React, { useCallback, useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { useFeatureToggle } from '@flopflip/react-redux';
import { getBooleanField, getDateWithoutTimezoneShift, getTextField } from '@lender/utils';
import { Button, Tooltip, Typography } from '@mui/material';
import { ButtonGroupWrapper } from '@sharedCR/coreDesignSystem/Buttons';
import ModalFormWrapper from '@sharedCR/coreDesignSystem/ModalFormWrapper';
import { documentsApi } from 'app/services/api';
import DocumentView from 'app/shared/components/document/DocumentView';
import { onAutocompleteChangeInForm } from 'app/shared/components/form/Autocomplete';
import FileUploadZone from 'app/shared/components/form/FileUploadZone';
import {
  AutocompleteCDS,
  CheckboxCDS,
  TextFieldCDS,
} from 'app/shared/componentsRestyled/coreDesignSystem/form';
import { ModalSize } from 'app/shared/componentsRestyled/coreDesignSystem/Modals';
import dayjs from 'dayjs';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import PropTypes from 'prop-types';
import { object } from 'yup';

import { COMPOSED_CLAIMS } from 'constants/composedClaims';
import { COMMON_DATE_FORMAT, COMMON_DATETIME_FORMAT } from 'constants/date';
import { DOCUMENT_CONTROL_PANEL_HEIGHT } from 'constants/documents';
import { FeatureFlag } from 'constants/featureFlags';
import { taskStatusOptions } from 'constants/options';
import { PAGE_CODES } from 'constants/pageCodes';
import { DataType } from 'constants/taskCategories';
import { MAX_FILES_COUNT_TO_UPLOAD, TaskAssignmentType, TaskStatus } from 'constants/tasks';
import { HORIZONTAL_ALIGNMENT, MODAL_BODY_DEFAULT_OFFSETS } from 'constants/ui';
import useForm from 'hooks/useForm';
import { buildDocumentLoanParams } from 'utils/documentPanes';
import { openFileFromBlob } from 'utils/file';

import {
  getMasterPermissionsToPage,
  selectIsBrokerCompany,
  selectIsClaimAllowed,
  selectIsSubCompanyUser,
} from 'store/auth/selectors';
import {
  openConfirmationDialogAction,
  openDialogAction,
  showMessageAction,
} from 'store/global/actions';
import {
  approveTaskAction as defaultApproveTaskAction,
  getLoanBrokerConditionByApplicationIdAction,
  getLoanConditionByConditionIdAction,
  rejectTaskAction as defaultRejectTaskAction,
  showMessageThatCurrentUserCantChangeLoanAction,
  updateTaskAsyncAction,
} from 'store/loanApplications/actions';
import {
  selectFormLockedFieldsMap,
  selectFormLockedPage,
  selectIsCurrentUserCanChangeLoan,
  selectIsLoanInitiatedWithErrors,
} from 'store/loanApplications/selectors';
import { selectTaskTypeById } from 'store/system/selectors';

import OpenDocumentButton from './components/OpenDocumentButton';
import TaskActionButton from './components/TaskActionButton';
import TaskInfoThread from './components/TaskInfoThread';
import ReasonForm from './ReasonForm';
import { footerStyles, tooltipSlotProps } from './TaskInfo.styles';
import { TaskInfoTestIds } from './TaskInfo.testIds';
import { getTaskStatusOptions, resolveTaskInfoClose } from './TaskInfo.utils';

const submitButtonBaseProps = {
  className: 'whitespace-nowrap',
  variant: 'contained',
};

const cancelButtonBaseProps = {
  className: 'whitespace-nowrap',
  variant: 'line',
};

const getTaskInfoFormSchema = ({ isUserNoteRequired }) =>
  object().shape({
    status: getTextField(),
    name: getTextField(),
    description: getTextField().nullable(),
    userNote: getTextField({ isRequired: isUserNoteRequired }).nullable(),
    submittedText: getTextField({ isRequired: false }).nullable(),
    isDocumentRequested: getBooleanField().nullable(),
  });

const documentViewClasses = {
  documentViewWrapper: {
    maxHeight: `calc(100vh - ${DOCUMENT_CONTROL_PANEL_HEIGHT + MODAL_BODY_DEFAULT_OFFSETS}px)`,
  },
};

const selectTaskClaims = state => ({
  isApproveRejectCancelAllowed: selectIsClaimAllowed(COMPOSED_CLAIMS.APPROVE_REJECT_CANCEL_TASK)(
    state,
  ),
  isUpdateStatusAllowed: selectIsClaimAllowed(COMPOSED_CLAIMS.UPDATE_STATUS_TASK)(state),
  isUploadDocumentsAllowed: selectIsClaimAllowed(COMPOSED_CLAIMS.UPLOAD_TO_TASK)(state),
});

const TaskInfo = withLoading(
  ({ isLoading, setIsLoading, formRef, pipelineId, task, formName, ...restProps }) => {
    const dispatch = useDispatch();

    const [files, setFiles] = useState([]);

    const { isEditAllowed } = useSelector(getMasterPermissionsToPage(PAGE_CODES.TASKS));
    const isLoanInitedWithErrors = useSelector(selectIsLoanInitiatedWithErrors);
    const isCurrentUserCanChangeLoan = useSelector(selectIsCurrentUserCanChangeLoan);
    const { isApproveRejectCancelAllowed, isUpdateStatusAllowed, isUploadDocumentsAllowed } =
      useSelector(selectTaskClaims, shallowEqual);
    const taskTypeRelatedData = useSelector(selectTaskTypeById(task?.taskTypeId));
    const isSubCompanyUser = useSelector(selectIsSubCompanyUser);
    const lockedFieldsMap = useSelector(selectFormLockedFieldsMap(formName));
    const isPageLocked = useSelector(selectFormLockedPage(formName));
    const isBrokerCompany = useSelector(selectIsBrokerCompany);

    const isUploadDocumentsToNewTasksAvailable = useFeatureToggle(
      FeatureFlag.EnableUploadDocumentsToNewTasks,
    );

    const {
      status,
      taskId,
      taskTypeId,
      lastModified,
      documentRequestedDate,
      documentRequestDeadline,
      applicationId,
      loanId,
      availableForAssignees,
      ...taskInfo
    } = task || {};

    const { taskAssignmentType, dataType, isDocumentRequestable } =
      taskTypeRelatedData || task.taskType || {};

    const {
      selectedTabStatus = null,
      approveTaskAction = defaultApproveTaskAction,
      rejectTaskAction = defaultRejectTaskAction,
      updateTaskAction = updateTaskAsyncAction,
      conditionId,
      // When TaskInfo is hosted inline (expandable row) instead of a dialog, the caller passes
      // onClose to collapse the row; the dialog usage leaves it undefined and keeps closing the
      // modal via closeDialogAction.
      onClose = null,
    } = restProps;

    const isSlaGroupAssigned = taskAssignmentType === TaskAssignmentType.SLA_GROUP;
    const isBorrowerAssigned = taskAssignmentType === TaskAssignmentType.BORROWER;
    const isSubCompanyAssigned = taskAssignmentType === TaskAssignmentType.SUB_COMPANY;
    const isFreeTextTaskType = dataType === DataType.FREE_TEXT;
    const isDocumentsTaskType = dataType === DataType.DOCUMENTS;
    const isFormsTaskType = dataType === DataType.FORMS;

    const isOngoingTab = (selectedTabStatus || status) === TaskStatus.NEW;

    // A canceled task may be restored (uncanceled) by a user with the approve/reject/cancel claim.
    const isCanceledTaskRestorable = status === TaskStatus.CANCELED && isApproveRejectCancelAllowed;

    const isFieldDisabled = isLoading || !isOngoingTab || isLoanInitedWithErrors || !isEditAllowed;
    const isUploadingDisabled = isLoading || !availableForAssignees;
    const isUploadingDisabledForSlaGroup = isUploadingDisabled || !isEditAllowed;

    const {
      form,
      handleChange,
      setInForm,
      validateField,
      setSchema,
      validateForm,
      validationErrors,
      isFormChanged,
    } = useForm({
      ...taskInfo,
      lastModified: dayjs(lastModified).format(COMMON_DATETIME_FORMAT),
      status: taskStatusOptions.find(({ value }) => value === status),
      documentRequestedDate:
        documentRequestedDate &&
        dayjs(getDateWithoutTimezoneShift(documentRequestedDate)).format(COMMON_DATE_FORMAT),
      documentRequestDeadline:
        documentRequestDeadline &&
        dayjs(getDateWithoutTimezoneShift(documentRequestDeadline)).format(COMMON_DATE_FORMAT),
    });

    const isApprovedOrSubmittedTask = [TaskStatus.SUBMITTED, TaskStatus.APPROVED].includes(
      form.status.value,
    );
    const isCanceledTask = form.status.value === TaskStatus.CANCELED;
    const isNewTask = form.status.value === TaskStatus.NEW;

    const isSubmittedTextEditable =
      (isSlaGroupAssigned || isSubCompanyAssigned) && isFreeTextTaskType;
    const isSubmittedTextRequired =
      isSubmittedTextEditable && !isCanceledTask && !isSlaGroupAssigned;
    const isSubmittedTextVisible =
      isSubmittedTextEditable || (isApprovedOrSubmittedTask && isFreeTextTaskType && !isOngoingTab);
    const isSubmittedDocumentsVisible =
      ((isApprovedOrSubmittedTask ||
        isSlaGroupAssigned ||
        (isUploadDocumentsToNewTasksAvailable && isNewTask)) &&
        isDocumentsTaskType) ||
      (isApprovedOrSubmittedTask && isFormsTaskType);

    const isDocumentsTypeSlaGroupTask = isSlaGroupAssigned && isDocumentsTaskType;
    const isDocumentsRequired = isDocumentsTypeSlaGroupTask && !isCanceledTask;

    const isOrderedFieldsVisible = isDocumentsTypeSlaGroupTask && isDocumentRequestable;

    const isUploadDocumentsSectionShown =
      isOngoingTab &&
      isDocumentsTaskType &&
      (isBorrowerAssigned || isSubCompanyAssigned) &&
      isUploadDocumentsAllowed &&
      (!isNewTask || isUploadDocumentsToNewTasksAvailable) &&
      !isCanceledTask;

    const submittedDocumentsCount = form.submittedDocuments?.length ?? 0;
    const totalFilesCount = files.length + submittedDocumentsCount;
    const isFilesCountInvalid =
      isUploadDocumentsSectionShown && totalFilesCount > MAX_FILES_COUNT_TO_UPLOAD;

    const isUpdateDisabled =
      isLoading ||
      !(files.length || isFormChanged) ||
      !isNewTask ||
      (isFilesCountInvalid && !isCanceledTask);

    // Restore (uncancel) is enabled once the user has picked a status other than Canceled.
    const isRestoreDisabled = isLoading || !isFormChanged || isCanceledTask;

    const hasSubmittedDocuments = !!submittedDocumentsCount;
    const canSubmitNewTaskWithDocuments =
      isUploadDocumentsToNewTasksAvailable &&
      isNewTask &&
      isDocumentsTaskType &&
      (!!files.length || hasSubmittedDocuments) &&
      isApprovedOrSubmittedTask;

    const isSubmitDisabled =
      isLoading ||
      (!isFormChanged && !canSubmitNewTaskWithDocuments) ||
      (isDocumentsRequired && !(files.length || hasSubmittedDocuments)) ||
      (isSubmittedTextRequired && !form.submittedText) ||
      (isFilesCountInvalid && !isCanceledTask) ||
      (isNewTask && !canSubmitNewTaskWithDocuments) ||
      (isUploadDocumentsSectionShown && !files.length && !hasSubmittedDocuments && !isCanceledTask);

    const isStatusSelectDisabled =
      isLoading ||
      isLoanInitedWithErrors ||
      !isEditAllowed ||
      (!isOngoingTab && !isCanceledTaskRestorable) ||
      (!isOngoingTab && isSubCompanyUser) ||
      !(isApproveRejectCancelAllowed || isUpdateStatusAllowed) ||
      (isUploadDocumentsSectionShown && !!files.length && !isUploadDocumentsToNewTasksAvailable);

    const isModalActionButtonsVisible =
      isEditAllowed && !isLoanInitedWithErrors && isCurrentUserCanChangeLoan;

    const statusOptions = getTaskStatusOptions({
      isApproveRejectCancelAllowed,
      isUpdateStatusAllowed,
      currentStatus: status,
    });

    useEffect(() => {
      const schema = getTaskInfoFormSchema({ isUserNoteRequired: isCanceledTask });
      setSchema(schema);
    }, [setSchema, isCanceledTask]);

    useEffect(() => {
      if (!isCurrentUserCanChangeLoan) {
        dispatch(showMessageThatCurrentUserCantChangeLoanAction());
      }
    }, [dispatch, isCurrentUserCanChangeLoan]);

    // Inline host collapses its row via onClose; dialog host closes the modal. Cancel and every
    // success path (approve / update / submit) route through here so both usages dismiss.
    const handleClose = useCallback(
      () => resolveTaskInfoClose({ onClose, dispatch }),
      [onClose, dispatch],
    );

    const onApproveTask = useCallback(
      e => {
        e.stopPropagation();

        const params = {
          message: 'Confirm task approval',
          description: 'Are you sure you want to approve this task?',
          onAccept: () => {
            setIsLoading(true);
            dispatch(approveTaskAction({ taskId }))
              .then(() => {
                dispatch(
                  showMessageAction({
                    message: 'Task approved',
                    variant: 'success',
                  }),
                );
                handleClose();
              })
              .catch(Error)
              .finally(() => setIsLoading(false));
          },
        };

        dispatch(openConfirmationDialogAction(params));
      },
      [taskId, dispatch, setIsLoading, approveTaskAction, handleClose],
    );

    const onOpenReasonForm = useCallback(() => {
      dispatch(
        openDialogAction({
          header: 'Rejection reason',
          body: dialogProps => (
            <ReasonForm
              {...dialogProps}
              formName={formName}
              rejectTaskAction={rejectTaskAction}
              taskId={taskId}
            />
          ),
        }),
      );
    }, [taskId, dispatch, rejectTaskAction, formName]);

    const updateTask = useCallback(
      overrideStatus => {
        const request = {
          status: overrideStatus || form.status.value,
          name: form.name,
          description: form.description,
          userNote: isCanceledTask ? form.userNote : null,
          formBundleId: form.formBundleId || null,
          isSignatureRequired: form.isSignatureRequired ?? null,
          submittedText: !files.length && form.submittedText ? form.submittedText : null,
          isDocumentRequested: isOngoingTab ? form.isDocumentRequested : null,
        };

        return dispatch(
          updateTaskAction({ taskId, pipelineId, taskTypeId, applicationId, request, files }),
        );
      },
      [
        files,
        form,
        taskTypeId,
        applicationId,
        isCanceledTask,
        dispatch,
        taskId,
        updateTaskAction,
        isOngoingTab,
        pipelineId,
      ],
    );

    const onUpdateOngoingTask = useCallback(async () => {
      const isValid = isFormChanged ? await validateForm() : files.length;

      if (!isValid) {
        return;
      }

      setIsLoading(true);
      updateTask()
        .then(() => {
          dispatch(showMessageAction({ message: 'Task updated', variant: 'success' }));
          handleClose();
        })
        .catch(Error)
        .finally(() => setIsLoading(false));
    }, [
      dispatch,
      validateForm,
      setIsLoading,
      files.length,
      updateTask,
      isFormChanged,
      handleClose,
    ]);

    const onSubmitOngoingTask = useCallback(async () => {
      const isValid = await validateForm({ noFormChangedValidate: true });

      if (!isValid) {
        return;
      }

      const statusAfterSubmit = isSlaGroupAssigned ? TaskStatus.APPROVED : TaskStatus.SUBMITTED;
      const taskStatus = isNewTask ? statusAfterSubmit : form.status.value;

      dispatch(
        openConfirmationDialogAction({
          message: 'Confirm submitting task',
          description: 'Are you sure you want to submit this information?',
          onAccept: () => {
            setIsLoading(true);
            updateTask(taskStatus)
              .then(() => {
                if (isBrokerCompany) {
                  return dispatch(getLoanBrokerConditionByApplicationIdAction({ applicationId }));
                }

                if (conditionId) {
                  return dispatch(
                    getLoanConditionByConditionIdAction({ loanConditionId: conditionId }),
                  );
                }
              })
              .then(() => {
                dispatch(
                  showMessageAction({
                    message: 'Task submitted',
                    variant: 'success',
                  }),
                );
                handleClose();
              })
              .catch(Error)
              .finally(() => setIsLoading(false));
          },
        }),
      );
    }, [
      applicationId,
      validateForm,
      isSlaGroupAssigned,
      form.status,
      dispatch,
      setIsLoading,
      updateTask,
      isNewTask,
      isBrokerCompany,
      conditionId,
      handleClose,
    ]);

    const onCloseDialog = useCallback(() => {
      handleClose();
    }, [handleClose]);

    const onOpenDocument = useCallback(
      e => {
        const { documentId, applicationId, loanId, fileName } = e.currentTarget.dataset;

        const loanParams = buildDocumentLoanParams({ loanId, applicationId });

        const onDownloadDocument = async () => {
          try {
            setIsLoading(true);

            const { data: pdfBlob } = await documentsApi.getContent({ documentId });
            await openFileFromBlob(pdfBlob, fileName);
          } catch {
            // do nothing
          } finally {
            setIsLoading(false);
          }
        };

        dispatch(
          openDialogAction({
            header: 'View Document',
            size: ModalSize.XL,
            hasScrollY: false,
            body: () => (
              <DocumentView
                isDownloadable
                isView
                applicationId={applicationId}
                classes={documentViewClasses}
                docId={documentId}
                isControlVisible={false}
                loanParams={loanParams}
                onDownloadDocument={onDownloadDocument}
              />
            ),
          }),
        );
      },
      [dispatch, setIsLoading],
    );

    const renderButtons = () => {
      switch (status) {
        case TaskStatus.NEW:
          return (
            <>
              <Button
                {...cancelButtonBaseProps}
                data-testid={TaskInfoTestIds.TASK_INFO_FORM_CANCEL_BUTTON}
                disabled={isLoading}
                onClick={onCloseDialog}
              >
                Cancel
              </Button>
              {(!isSubCompanyUser || isUploadDocumentsToNewTasksAvailable) && (
                <Button
                  {...submitButtonBaseProps}
                  data-testid={TaskInfoTestIds.TASK_INFO_FORM_UPDATE_BUTTON}
                  disabled={isUpdateDisabled}
                  onClick={onUpdateOngoingTask}
                >
                  Update
                </Button>
              )}
              <Tooltip
                className="ml-auto"
                placement="top"
                slotProps={tooltipSlotProps}
                title={
                  isSubmitDisabled && isSlaGroupAssigned
                    ? 'Task can be submitted when all task data is provided'
                    : ''
                }
              >
                <span>
                  <Button
                    {...submitButtonBaseProps}
                    data-testid={TaskInfoTestIds.TASK_INFO_FORM_SUBMIT_BUTTON}
                    disabled={isSubmitDisabled}
                    onClick={onSubmitOngoingTask}
                  >
                    Submit
                  </Button>
                </span>
              </Tooltip>
            </>
          );

        case TaskStatus.SUBMITTED:
          return (
            !isSubCompanyUser &&
            isApproveRejectCancelAllowed && (
              <>
                <Button
                  {...submitButtonBaseProps}
                  data-testid={TaskInfoTestIds.TASK_INFO_FORM_APPROVE_BUTTON}
                  onClick={onApproveTask}
                >
                  Approve
                </Button>

                <Button
                  {...submitButtonBaseProps}
                  data-testid={TaskInfoTestIds.TASK_INFO_FORM_REJECT_BUTTON}
                  onClick={onOpenReasonForm}
                >
                  Reject
                </Button>
              </>
            )
          );

        case TaskStatus.CANCELED:
          return (
            isCanceledTaskRestorable && (
              <>
                <Button
                  {...cancelButtonBaseProps}
                  data-testid={TaskInfoTestIds.TASK_INFO_FORM_CANCEL_BUTTON}
                  disabled={isLoading}
                  onClick={onCloseDialog}
                >
                  Cancel
                </Button>
                <Button
                  {...submitButtonBaseProps}
                  data-testid={TaskInfoTestIds.TASK_INFO_FORM_RESTORE_BUTTON}
                  disabled={isRestoreDisabled}
                  onClick={onUpdateOngoingTask}
                >
                  Restore
                </Button>
              </>
            )
          );

        default:
          return null;
      }
    };

    const onAutocompleteChange = useCallback(
      (e, option) => {
        if (
          isUploadDocumentsSectionShown &&
          option.value !== (isSubCompanyUser ? TaskStatus.SUBMITTED : TaskStatus.APPROVED) &&
          !(isUploadDocumentsToNewTasksAvailable && option.value === TaskStatus.NEW)
        ) {
          setFiles([]);
        }

        onAutocompleteChangeInForm({ setInForm, validateField })(e, option);
      },
      [
        isUploadDocumentsSectionShown,
        setInForm,
        validateField,
        isSubCompanyUser,
        isUploadDocumentsToNewTasksAvailable,
      ],
    );

    const saveBorrowerDocuments = useCallback(
      files => {
        if (files.length && !(isUploadDocumentsToNewTasksAvailable && isNewTask)) {
          setInForm(
            'status',
            taskStatusOptions.find(
              ({ value }) =>
                value === (isSubCompanyUser ? TaskStatus.SUBMITTED : TaskStatus.APPROVED),
            ),
          );
        }

        setFiles(files);
      },
      [setInForm, isSubCompanyUser, isUploadDocumentsToNewTasksAvailable, isNewTask],
    );

    return (
      <ModalFormWrapper
        isWithCustomButtons
        formRef={formRef}
        formTestId="task-info-form"
        isFormChanged={isFormChanged}
        isSubmitting={isLoading}
      >
        <TextFieldCDS
          fullWidth
          required
          className="mb-24"
          data-testid={TaskInfoTestIds.TASK_INFO_FORM_NAME_INPUT}
          disabled={isFieldDisabled || !isSlaGroupAssigned || isPageLocked || lockedFieldsMap.name}
          error={!!validationErrors.name}
          helperText={validationErrors.name}
          id="name"
          label="Name"
          name="name"
          type="text"
          value={form.name}
          variant="outlined"
          onChange={handleChange}
        />

        <AutocompleteCDS
          containerClassName="mb-24"
          dataTestId={TaskInfoTestIds.TASK_INFO_FORM_STATUS_SELECT}
          disabled={isStatusSelectDisabled || isPageLocked || lockedFieldsMap.status}
          error={!!validationErrors.status}
          helperText={validationErrors.status}
          label="State"
          name="status"
          options={statusOptions}
          value={form.status || ''}
          onChange={onAutocompleteChange}
        />

        <TextFieldCDS
          fullWidth
          multiline
          required
          className="mb-24"
          data-testid={TaskInfoTestIds.TASK_INFO_FORM_DESCRIPTION_INPUT}
          disabled={
            isFieldDisabled || isSubCompanyUser || isPageLocked || lockedFieldsMap.description
          }
          error={!!validationErrors.description}
          helperText={validationErrors.description}
          id="description"
          label="Description"
          maxLength={4000}
          name="description"
          rows={4}
          type="text"
          value={form.description || ''}
          variant="outlined"
          onChange={handleChange}
        />

        {isCanceledTask && (
          <TextFieldCDS
            fullWidth
            multiline
            required
            className="mb-24"
            data-testid={TaskInfoTestIds.TASK_INFO_FORM_USER_NOTE_INPUT}
            disabled={isFieldDisabled || isPageLocked || lockedFieldsMap.cancelationReason}
            error={!!validationErrors.userNote}
            helperText={validationErrors.userNote}
            id="userNote"
            label="Cancelation Reason"
            maxLength={450}
            name="userNote"
            rows={4}
            type="text"
            value={form.userNote ?? ''}
            variant="outlined"
            onChange={handleChange}
          />
        )}

        {isSubmittedTextVisible && (
          <TextFieldCDS
            fullWidth
            multiline
            className="mb-24"
            data-testid={TaskInfoTestIds.TASK_INFO_FORM_SUBMITTED_TEXT_INPUT}
            disabled={
              isFieldDisabled ||
              !isSubmittedTextEditable ||
              isPageLocked ||
              lockedFieldsMap.submittedText
            }
            error={!!validationErrors.submittedText}
            helperText={validationErrors.submittedText}
            id="submittedText"
            label="Submitted Text"
            name="submittedText"
            rows={4}
            type="text"
            value={form.submittedText}
            variant="outlined"
            onChange={handleChange}
          />
        )}

        <TextFieldCDS
          disabled
          fullWidth
          className="mb-24"
          data-testid={TaskInfoTestIds.TASK_INFO_FORM_LAST_MODIFIED_INPUT}
          id="lastModified"
          label="Last Modified Date"
          name="lastModified"
          type="text"
          value={form.lastModified}
          variant="outlined"
        />

        {isSubmittedDocumentsVisible &&
          form.submittedDocuments?.map(({ id, fileName }) => (
            <OpenDocumentButton
              applicationId={applicationId}
              fileName={fileName}
              id={id}
              key={id}
              loanId={loanId}
              onOpenDocument={onOpenDocument}
            />
          ))}

        {isDocumentsTypeSlaGroupTask && isOngoingTab && (
          <FileUploadZone
            isMultiple
            noValidate
            accept=".pdf"
            className="flex flex-col justify-center"
            disabled={isUploadingDisabledForSlaGroup || isPageLocked || lockedFieldsMap.slaFiles}
            getFiles={setFiles}
            isButtonsShown={false}
            isFilesPreviewShown={false}
          />
        )}

        {isUploadDocumentsSectionShown && (
          <>
            <FileUploadZone
              isMultiple
              accept="application/pdf,image/png,image/jpg,image/jpeg"
              className="flex flex-col justify-center"
              disabled={isUploadingDisabled || isPageLocked || lockedFieldsMap.borrowerFiles}
              getFiles={saveBorrowerDocuments}
              isButtonsShown={false}
              isFilesPreviewShown={false}
            />
            <Typography
              className="-mt-12 mb-8 text-center"
              color={isFilesCountInvalid ? 'error' : 'textSecondary'}
              variant="regular14"
            >
              You can add not more than {MAX_FILES_COUNT_TO_UPLOAD} files
            </Typography>
          </>
        )}

        {isOrderedFieldsVisible && (
          <>
            {isOngoingTab && (
              <CheckboxCDS
                fullWidth
                data-testid={TaskInfoTestIds.TASK_INFO_FORM_IS_ORDERED_CHECKBOX}
                disabled={
                  isLoading || !isEditAllowed || isPageLocked || lockedFieldsMap.isDocumentRequested
                }
                error={!!validationErrors.isDocumentRequested}
                helperText={validationErrors.isDocumentRequested}
                label="Ordered"
                name="isDocumentRequested"
                value={form.isDocumentRequested}
                onChange={handleChange}
              />
            )}

            {form.isDocumentRequested && (
              <>
                <TextFieldCDS
                  disabled
                  className="mb-24"
                  data-testid={TaskInfoTestIds.TASK_INFO_FORM_ORDERED_DATE}
                  label="Ordered Date"
                  name="orderedDate"
                  type="text"
                  value={form.documentRequestedDate}
                  variant="outlined"
                />

                <div className="flex justify-between gap-48">
                  <TextFieldCDS
                    disabled
                    data-testid={TaskInfoTestIds.TASK_INFO_FORM_EXPECTED_DATE}
                    label="Expected date"
                    name="expectedDate"
                    type="text"
                    value={form.documentRequestDeadline}
                    variant="outlined"
                  />
                  <TextFieldCDS
                    disabled
                    fullWidth
                    data-testid={TaskInfoTestIds.TASK_INFO_FORM_BUSINESS_DAYS_TURNAROUND_INPUT}
                    label="Business Day(s) Turnaround"
                    name="businessDaysTurnaround"
                    type="text"
                    value={form.documentRequestDays}
                    variant="outlined"
                  />
                </div>
              </>
            )}
          </>
        )}

        {taskId && <TaskInfoThread isLocked={isPageLocked} taskId={taskId} />}

        <ButtonGroupWrapper alignment={HORIZONTAL_ALIGNMENT.RIGHT} sx={footerStyles}>
          <TaskActionButton isHidden={!isModalActionButtonsVisible} task={task} />
          {isModalActionButtonsVisible && renderButtons()}
        </ButtonGroupWrapper>
      </ModalFormWrapper>
    );
  },
);

TaskInfo.propTypes = {
  task: PropTypes.object.isRequired,
  approveTaskAction: PropTypes.func,
  rejectTaskAction: PropTypes.func,
  updateTaskAction: PropTypes.func,
  selectedTabStatus: PropTypes.string,
  formName: PropTypes.string,
  ...WithLoadingPropTypes,
};

export default TaskInfo;
